// #67, list and map side by side on a wide screen: compact numbered rows on
// the left, the numbered pins on the map at the right. Pointing at a row
// lifts its pin; tapping a pin lights its row and brings it into view (the
// row carries the `stay-hub-card-` id the map's pick handler scrolls to).
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { activityHrefParts } from "../lib/activity-href";
import { openStateAt, useClientNow } from "../hooks/use-client-now";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GOLD = "#E5B265";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";
const CONTROL = { background: "linear-gradient(180deg, #FFFFFF 0%, #F7F7FB 100%)", boxShadow: "0 0 0 1px rgba(17,14,57,0.16)" } as const;

export interface MapRowItem {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  images: string[];
  mode?: string;
  segment?: string | null;
  openingHours?: unknown;
  category: { name: string; slug: string } | null;
  city: { name: string; slug: string };
}

/** The open dot: green open, gold closing soon, grey closed. Nothing when unknown. */
const DOT: Record<string, string> = { open: "#1FA855", closing_soon: GOLD, closed: "#9a9bb5" };

export function HubMapRows({
  items,
  activeId,
  onHover,
  lang,
  linkSearch,
}: {
  items: MapRowItem[];
  activeId: string | null;
  onHover: (id: string | null) => void;
  lang: string;
  linkSearch?: Record<string, string | number>;
}) {
  const { t } = useTranslation();
  // The dots read the clock, so only after mount (no hydration mismatch).
  const now = useClientNow();
  return (
    <ol className="m-0 flex list-none flex-col gap-1.5 p-0" data-testid="hub-map-rows">
      {items.map((a, k) => {
        const on = a.id === activeId;
        const href = activityHrefParts({
          lang,
          citySlug: a.city.slug,
          categorySlug: a.category?.slug ?? null,
          segment: a.mode === "explore" ? a.segment ?? null : null,
          slug: a.slug,
        });
        const state = openStateAt(a.openingHours, now);
        const sub = a.subtitle?.trim() || [a.category?.name, a.city.name].filter(Boolean).join(" · ");
        return (
          <li key={a.id} id={`stay-hub-card-${a.id}`}>
            <Link
              to={href.to}
              params={href.params}
              search={(a.mode !== "explore" ? linkSearch : undefined) as never}
              onMouseEnter={() => onHover(a.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(a.id)}
              onBlur={() => onHover(null)}
              aria-current={on ? "true" : undefined}
              className="flex items-center gap-3 rounded-[14px] p-1.5 text-start no-underline transition-[box-shadow,background] duration-200"
              style={on ? { ...CONTROL, boxShadow: "0 0 0 1.5px rgba(17,14,57,0.5)" } : undefined}
            >
              <span
                aria-hidden="true"
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold tabular-nums"
                style={on ? { background: NAVY_GRADIENT, color: GOLD } : { ...CONTROL, color: NAVY }}
              >
                {k + 1}
              </span>
              {a.images[0] ? (
                <img src={a.images[0]} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-[11px] object-cover" />
              ) : (
                <span aria-hidden="true" className="h-14 w-14 shrink-0 rounded-[11px]" style={{ background: "#EEF0F6" }} />
              )}
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 break-words text-[13.5px] font-semibold" style={{ color: NAVY }}>
                  {a.title}
                </span>
                <span className="flex min-w-0 items-center gap-1.5 text-[11.5px]" style={{ color: MUTED }}>
                  {state ? (
                    <span
                      role="img"
                      aria-label={t(`explore.open_state.${state}`)}
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: DOT[state] }}
                      data-testid={`open-dot-${state}`}
                    />
                  ) : null}
                  <span className="min-w-0 line-clamp-2">{sub}</span>
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}
